import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** CMS page hit (aligned with mot-website-client SearchResultItem). */
export interface WebsiteCmsSearchResultItem {
  id: number;
  title: string;
  description: string;
  urlSlug: string;
}

interface SearchResultApiItem {
  id: number;
  title: string;
  description: string;
  urlSlug: string;
  pageTranslationTags?: Array<{
    id?: number;
    name?: string;
    tag?: { id: number; name: string };
  }>;
}

interface SearchResultApiResponse {
  items: SearchResultApiItem[];
  totalItems: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

function joinWebsiteBasePath(websiteBaseUrl: string, path: string): string {
  const base = websiteBaseUrl.replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

/** Full URL to a CMS page on the public site (new window). Hebrew has no locale prefix. */
export function buildMotWebsitePageUrl(
  websiteBaseUrl: string,
  urlSlug: string,
  languageSymbol: string
): string {
  const slug = (urlSlug || '').replace(/^\/+/, '');
  const path =
    languageSymbol === 'he' ? `/${slug}` : `/${languageSymbol}/${slug}`;
  return joinWebsiteBasePath(websiteBaseUrl, path);
}

/** Full URL to site search with query (new window). */
export function buildMotWebsiteSearchUrl(
  websiteBaseUrl: string,
  query: string,
  languageSymbol: string
): string {
  const q = encodeURIComponent(query);
  const path =
    languageSymbol === 'he'
      ? `/search?q=${q}`
      : `/${languageSymbol}/search?q=${q}`;
  return joinWebsiteBasePath(websiteBaseUrl, path);
}

@Injectable({ providedIn: 'root' })
export class WebsiteCmsSearchService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Same endpoint as mot-website-client _CmsApiService.searchPages (BO BE API).
   */
  search(
    languageId: number,
    searchTerm: string,
    page = 1,
    limit = 10
  ): Observable<WebsiteCmsSearchResultItem[]> {
    const trimmed = searchTerm?.trim() ?? '';
    if (trimmed.length < 2) {
      return of([]);
    }
    const params = new HttpParams()
      .set('searchTerm', trimmed)
      .set('page', String(page))
      .set('limit', String(limit));
    const url = `${environment.bo_be_service_url}content-management/search/language/${languageId}`;
    const termLower = trimmed.toLowerCase();
    return this.http.get<SearchResultApiResponse>(url, { params }).pipe(
      map((apiResponse) => {
        const items = apiResponse?.items ?? [];
        const mapped: WebsiteCmsSearchResultItem[] = items.map((item) => ({
          id: item.id,
          title: item.title,
          description: item.description || '',
          urlSlug: item.urlSlug,
        }));
        return mapped.sort((a, b) => {
          const aTitle = a.title.toLowerCase().includes(termLower);
          const bTitle = b.title.toLowerCase().includes(termLower);
          if (aTitle === bTitle) {
            return 0;
          }
          return aTitle ? -1 : 1;
        });
      }),
      catchError(() => of([]))
    );
  }

  openPageResultInNewWindow(
    item: WebsiteCmsSearchResultItem,
    languageSymbol: string
  ): void {
    const url = buildMotWebsitePageUrl(
      environment.website_url,
      item.urlSlug,
      languageSymbol
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  openFullSearchInNewWindow(query: string, languageSymbol: string): void {
    const url = buildMotWebsiteSearchUrl(
      environment.website_url,
      query,
      languageSymbol
    );
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
